import type { Fill, GradientStop } from "../types";

/** A solid fill helper. */
export function solidFill(color: string): Fill {
  return { kind: "solid", color };
}

/** A gradient fill helper. */
export function gradientFill(stops: GradientStop[], angle = 90): Fill {
  return { kind: "gradient", stops, angle };
}

/**
 * Apply the gradient's spread + offset to a raw stop position (0..1).
 * Kept as a standalone function so both the canvas resolver and the
 * CSS preview go through the same math.
 */
function transformStopPos(pos: number, spread: number, offset: number): number {
  const out = (pos - 0.5) * spread + 0.5 + offset;
  return Math.max(0, Math.min(1, out));
}

function spreadOf(fill: Extract<Fill, { kind: "gradient" }>): number {
  const v = fill.spread;
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : 1;
}

function offsetOf(fill: Extract<Fill, { kind: "gradient" }>): number {
  const v = fill.offset;
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

/**
 * Produce a CSS `background` value for the fill so React swatches and
 * previews can render it with no extra canvas code. For gradients we
 * build a `linear-gradient(...)` string using our `angle` convention
 * (0 = →, 90 = ↓). CSS `linear-gradient`'s `0deg` is bottom-to-top, so
 * we convert with `angle + 90` to match our convention.
 */
export function fillToCss(fill: Fill): string {
  if (fill.kind === "solid") return fill.color;
  const spread = spreadOf(fill);
  const offset = offsetOf(fill);
  const sorted = [...fill.stops].sort((a, b) => a.pos - b.pos);
  const stopStrs = sorted.map((s) => {
    const p = transformStopPos(s.pos, spread, offset);
    return `${s.color} ${Math.round(p * 10000) / 100}%`;
  });
  const cssAngle = fill.angle + 90;
  return `linear-gradient(${cssAngle}deg, ${stopStrs.join(", ")})`;
}

/**
 * Apply a fill as the current `fillStyle` on a canvas context for
 * filling a rectangle whose centre is `(cx, cy)` with size `w × h`.
 * For solids this is just a color string; for gradients it constructs
 * a `CanvasGradient` spanning the rectangle at the configured angle.
 *
 * Returns the value to assign to `ctx.fillStyle`.
 */
export function resolveFillStyle(
  ctx: CanvasRenderingContext2D,
  fill: Fill,
  cx: number,
  cy: number,
  w: number,
  h: number,
): string | CanvasGradient {
  if (fill.kind === "solid") return fill.color;
  if (!fill.stops.length) return "#000000";
  // A linear gradient that stretches across the bounding box along
  // `angle`. Half-diagonal length guarantees full coverage for any
  // angle (including 45° across a non-square box).
  const rad = (fill.angle * Math.PI) / 180;
  const r = Math.sqrt(w * w + h * h) / 2;
  const dx = Math.cos(rad) * r;
  const dy = Math.sin(rad) * r;
  const g = ctx.createLinearGradient(cx - dx, cy - dy, cx + dx, cy + dy);
  const spread = spreadOf(fill);
  const offset = offsetOf(fill);
  const sorted = [...fill.stops].sort((a, b) => a.pos - b.pos);
  for (const s of sorted) {
    const pos = transformStopPos(s.pos, spread, offset);
    g.addColorStop(pos, s.color);
  }
  return g;
}

/** Deep clone a Fill so stop arrays aren't shared between layers. */
export function cloneFill(fill: Fill): Fill {
  if (fill.kind === "solid") return { kind: "solid", color: fill.color };
  const out: Extract<Fill, { kind: "gradient" }> = {
    kind: "gradient",
    angle: fill.angle,
    stops: fill.stops.map((s) => ({ pos: s.pos, color: s.color })),
  };
  if (typeof fill.spread === "number") out.spread = fill.spread;
  if (typeof fill.offset === "number") out.offset = fill.offset;
  return out;
}

// -----------------------------------------------------------------
// Presets
// -----------------------------------------------------------------

/** Solid-color preset swatches shown above the color picker. */
export const SOLID_PRESETS: readonly string[] = [
  "#17191c", // app default / near-black panel tone
  "#000000",
  "#ffffff",
  "#ef4444", // red
  "#f97316", // orange
  "#facc15", // yellow
  "#22c55e", // green
  "#06b6d4", // cyan
  "#3b82f6", // blue
  "#a855f7", // violet
  "#ec4899", // pink
];

/** Gradient preset buttons shown in the gradient editor. */
export const GRADIENT_PRESETS: readonly { label: string; fill: Fill }[] = [
  {
    label: "Rainbow",
    fill: gradientFill(
      [
        { pos: 0, color: "#ff0000" },
        { pos: 1 / 6, color: "#ff9900" },
        { pos: 2 / 6, color: "#ffee00" },
        { pos: 3 / 6, color: "#33cc33" },
        { pos: 4 / 6, color: "#3399ff" },
        { pos: 5 / 6, color: "#6633cc" },
        { pos: 1, color: "#cc33cc" },
      ],
      0,
    ),
  },
  {
    label: "Pastel Rainbow",
    fill: gradientFill(
      [
        { pos: 0, color: "#ffb3ba" },
        { pos: 0.2, color: "#ffdfba" },
        { pos: 0.4, color: "#ffffba" },
        { pos: 0.6, color: "#baffc9" },
        { pos: 0.8, color: "#bae1ff" },
        { pos: 1, color: "#d5baff" },
      ],
      0,
    ),
  },
  {
    label: "Sunset",
    fill: gradientFill(
      [
        { pos: 0, color: "#ff5f6d" },
        { pos: 1, color: "#ffc371" },
      ],
      90,
    ),
  },
  {
    label: "Sunrise",
    fill: gradientFill(
      [
        { pos: 0, color: "#ff9a44" },
        { pos: 0.5, color: "#fc6076" },
        { pos: 1, color: "#4a00e0" },
      ],
      90,
    ),
  },
  {
    label: "Ocean",
    fill: gradientFill(
      [
        { pos: 0, color: "#2e3192" },
        { pos: 1, color: "#1bffff" },
      ],
      90,
    ),
  },
  {
    label: "Fire",
    fill: gradientFill(
      [
        { pos: 0, color: "#ffe53b" },
        { pos: 1, color: "#ff2525" },
      ],
      90,
    ),
  },
  {
    label: "Lava",
    fill: gradientFill(
      [
        { pos: 0, color: "#1a0000" },
        { pos: 0.5, color: "#8a0303" },
        { pos: 1, color: "#ffa200" },
      ],
      90,
    ),
  },
  {
    label: "Mono",
    fill: gradientFill(
      [
        { pos: 0, color: "#000000" },
        { pos: 1, color: "#ffffff" },
      ],
      90,
    ),
  },
  {
    label: "Royal",
    fill: gradientFill(
      [
        { pos: 0, color: "#141e30" },
        { pos: 1, color: "#243b55" },
      ],
      90,
    ),
  },
  {
    label: "Candy",
    fill: gradientFill(
      [
        { pos: 0, color: "#ff9a9e" },
        { pos: 0.5, color: "#fad0c4" },
        { pos: 1, color: "#fbc2eb" },
      ],
      0,
    ),
  },
  {
    label: "Neon",
    fill: gradientFill(
      [
        { pos: 0, color: "#00f260" },
        { pos: 1, color: "#0575e6" },
      ],
      90,
    ),
  },
  {
    label: "Cyberpunk",
    fill: gradientFill(
      [
        { pos: 0, color: "#ff00c8" },
        { pos: 1, color: "#00ffe7" },
      ],
      45,
    ),
  },
  {
    label: "Vaporwave",
    fill: gradientFill(
      [
        { pos: 0, color: "#ff6ec4" },
        { pos: 1, color: "#7873f5" },
      ],
      45,
    ),
  },
  {
    label: "Galaxy",
    fill: gradientFill(
      [
        { pos: 0, color: "#0f0c29" },
        { pos: 0.5, color: "#302b63" },
        { pos: 1, color: "#24243e" },
      ],
      90,
    ),
  },
  {
    label: "Midnight",
    fill: gradientFill(
      [
        { pos: 0, color: "#000000" },
        { pos: 1, color: "#1e3a8a" },
      ],
      90,
    ),
  },
  {
    label: "Ice",
    fill: gradientFill(
      [
        { pos: 0, color: "#e0f7ff" },
        { pos: 1, color: "#3aa0ff" },
      ],
      90,
    ),
  },
  {
    label: "Emerald",
    fill: gradientFill(
      [
        { pos: 0, color: "#0f3d3e" },
        { pos: 1, color: "#55e6c1" },
      ],
      90,
    ),
  },
  {
    label: "Forest",
    fill: gradientFill(
      [
        { pos: 0, color: "#134e5e" },
        { pos: 1, color: "#71b280" },
      ],
      90,
    ),
  },
  {
    label: "Gold",
    fill: gradientFill(
      [
        { pos: 0, color: "#b8860b" },
        { pos: 0.5, color: "#ffd700" },
        { pos: 1, color: "#8b6914" },
      ],
      45,
    ),
  },
  {
    label: "Silver",
    fill: gradientFill(
      [
        { pos: 0, color: "#bdc3c7" },
        { pos: 0.5, color: "#ffffff" },
        { pos: 1, color: "#2c3e50" },
      ],
      45,
    ),
  },
  {
    label: "Berry",
    fill: gradientFill(
      [
        { pos: 0, color: "#8e2de2" },
        { pos: 1, color: "#4a00e0" },
      ],
      90,
    ),
  },
  {
    label: "Peach",
    fill: gradientFill(
      [
        { pos: 0, color: "#ffecd2" },
        { pos: 1, color: "#fcb69f" },
      ],
      90,
    ),
  },
  {
    label: "Lime",
    fill: gradientFill(
      [
        { pos: 0, color: "#a8ff78" },
        { pos: 1, color: "#78ffd6" },
      ],
      90,
    ),
  },
  {
    label: "Coral",
    fill: gradientFill(
      [
        { pos: 0, color: "#ff7e5f" },
        { pos: 1, color: "#feb47b" },
      ],
      90,
    ),
  },
];
